import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const HealthHistoryForm = ({ data, onUpdate }) => {
  const [formData, setFormData] = useState({
    medicalConditions: data?.healthHistory?.medicalConditions || [],
    currentMedications: data?.healthHistory?.currentMedications || '',
    allergies: data?.healthHistory?.allergies || '',
    bloodType: data?.healthHistory?.bloodType || '',
    emergencyContact: data?.healthHistory?.emergencyContact || '',
    emergencyPhone: data?.healthHistory?.emergencyPhone || '',
    ...data?.healthHistory
  });

  const medicalConditions = [
    'Heart Disease', 'High Blood Pressure', 'Diabetes', 'Asthma',
    'Arthritis', 'Cancer', 'HIV/AIDS', 'Hepatitis',
    'Kidney Disease', 'Liver Disease', 'Stroke', 'Seizures',
    'Bleeding Disorders', 'Thyroid Problems', 'Pregnancy'
  ];

  const bloodTypes = [
    { value: 'a-positive', label: 'A+' },
    { value: 'a-negative', label: 'A-' },
    { value: 'b-positive', label: 'B+' },
    { value: 'b-negative', label: 'B-' },
    { value: 'ab-positive', label: 'AB+' },
    { value: 'ab-negative', label: 'AB-' },
    { value: 'o-positive', label: 'O+' },
    { value: 'o-negative', label: 'O-' },
    { value: 'unknown', label: 'Unknown' }
  ];

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onUpdate('healthHistory', updated);
  };

  const handleConditionToggle = (condition) => {
    const updated = formData?.medicalConditions?.includes(condition)
      ? formData?.medicalConditions?.filter(c => c !== condition)
      : [...formData?.medicalConditions, condition];
    handleChange('medicalConditions', updated);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="Heart" size={24} className="text-primary" />
          Medical History
        </h2>
        <p className="text-sm text-muted-foreground">
          Please provide your complete medical history to ensure safe and effective treatment.
        </p>
      </div>

      {/* Medical Conditions */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Do you have or have you had any of the following conditions?
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {medicalConditions?.map((condition) => (
            <div key={condition} className="flex items-center gap-2">
              <Checkbox
                checked={formData?.medicalConditions?.includes(condition)}
                onChange={() => handleConditionToggle(condition)}
              />
              <label className="text-sm text-foreground cursor-pointer">
                {condition}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Current Medications */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Current Medications
        </label>
        <textarea
          value={formData?.currentMedications}
          onChange={(e) => handleChange('currentMedications', e?.target?.value)}
          placeholder="List all medications you are currently taking, including dosages..."
          className="w-full min-h-[100px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Include prescription medications, over-the-counter drugs, vitamins, and supplements
        </p>
      </div>

      {/* Allergies */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Allergies <span className="text-destructive">*</span>
        </label>
        <textarea
          value={formData?.allergies}
          onChange={(e) => handleChange('allergies', e?.target?.value)}
          placeholder="List any allergies to medications, foods, latex, or other substances..."
          className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          required
        />
        <p className="text-xs text-muted-foreground mt-1">
          If none, please write "None" or "No known allergies"
        </p>
      </div>

      {/* Blood Type */}
      <Select
        label="Blood Type"
        options={bloodTypes}
        value={formData?.bloodType}
        onChange={(value) => handleChange('bloodType', value)}
        placeholder="Select your blood type"
      />

      {/* Emergency Contact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Emergency Contact Name"
          type="text"
          value={formData?.emergencyContact}
          onChange={(e) => handleChange('emergencyContact', e?.target?.value)}
          placeholder="Full name"
          required
        />
        <Input
          label="Emergency Contact Phone"
          type="tel"
          value={formData?.emergencyPhone}
          onChange={(e) => handleChange('emergencyPhone', e?.target?.value)}
          placeholder="(555) 123-4567"
          required
        />
      </div>

      {/* Additional Information */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Additional Medical Information
        </label>
        <textarea
          value={formData?.additionalInfo || ''}
          onChange={(e) => handleChange('additionalInfo', e?.target?.value)}
          placeholder="Any other medical information we should know about..."
          className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
      </div>
    </div>
  );
};

export default HealthHistoryForm;